import { useFrame, useThree } from '@react-three/fiber';
import { type FC, useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { type Sprite, Vector2, Vector3 } from 'three';
import { ViewerLayer } from './ViewerLayer';
import { CALLBACK_ORDER_BILLBOARD } from './FrameCallbackOrder';

function nearestPowerOf2(n: number) {
  return 1 << (31 - Math.clz32(n));
}

function makeLabelCanvas(
  baseWidth: number,
  size: number,
  name: string,
  color: string,
  backgroundColor: string
) {
  const borderSize = 2;
  const context = document.createElement('canvas').getContext('2d')!;
  const font = `${size}px bold sans-serif`;
  context.font = font;
  // measure how long the name will be
  const textWidth = context.measureText(name).width;

  const doubleBorderSize = borderSize * 2;
  const width = 2 * nearestPowerOf2(baseWidth + doubleBorderSize);
  const height = 2 * nearestPowerOf2(size + doubleBorderSize);
  context.canvas.width = width;
  context.canvas.height = height;

  // need to set font again after resizing canvas
  context.font = font;
  context.textBaseline = 'middle';
  context.textAlign = 'center';

  context.fillStyle = backgroundColor;
  context.fillRect(0, 0, width, height);

  // scale to fit but don't stretch
  const scaleFactor = Math.min(1, baseWidth / textWidth);
  context.translate(width / 2, height / 2);
  context.scale(scaleFactor, scaleFactor);
  context.fillStyle = color;
  context.fillText(name, 0, 0);

  return context.canvas;
}

export interface TextBillboardProps {
  text: string;
  color: string;
  backgroundColor: string;
  fontSize: number;
  baseWidth: number;
  position: Parameters<THREE.Vector3['set']>;
  fixedSize?: number;
  clickable?: boolean;
}

export const TextBillboard: FC<TextBillboardProps> = ({
  text,
  color,
  backgroundColor,
  fontSize,
  baseWidth,
  position,
  fixedSize,
  clickable = false, // false by default
}) => {
  const sprite = useRef<Sprite | null>(null);
  const htmlCanvas = useMemo<HTMLCanvasElement>(() => {
    return makeLabelCanvas(baseWidth, fontSize, text, color, backgroundColor);
  }, [baseWidth, fontSize, text, color, backgroundColor]);

  const labelBaseScale = 1 / 128;

  const self = useRef<{ position: Vector3; cameraPosition: Vector3; screenSize: Vector2 }>({
    position: new Vector3(...position),
    cameraPosition: new Vector3(0, 0, 0),
    screenSize: new Vector2(),
  });

  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  useEffect(() => {
    if (sprite.current && clickable) {
      sprite.current.layers.enable(ViewerLayer.AutoPickable);
    }
  }, [clickable]);

  useFrame(() => {
    let scale = 1;

    if (fixedSize && sprite.current) {
      if (camera instanceof THREE.OrthographicCamera) {
        scale = (128 * fixedSize) / (fontSize * camera.zoom);
      } else if (camera instanceof THREE.PerspectiveCamera) {
        camera.getWorldPosition(self.current.cameraPosition);
        sprite.current.getWorldPosition(self.current.position);

        const distance = self.current.cameraPosition.distanceTo(self.current.position);

        gl.getSize(self.current.screenSize);

        const screenHalfHeight = 0.5 * self.current.screenSize.height;
        const halfFov = (camera.fov * Math.PI) / 360;

        // slope == height / distance
        const slope = Math.tan(halfFov);

        const screenDistribution = screenHalfHeight / slope;

        scale = (fixedSize * distance) / screenDistribution;
      }
      sprite.current?.scale.set(
        htmlCanvas.width * labelBaseScale * scale,
        htmlCanvas.height * labelBaseScale * scale,
        scale
      );
    }
  }, CALLBACK_ORDER_BILLBOARD);

  return (
    <sprite
      ref={sprite}
      position={position}
      scale={[htmlCanvas.width * labelBaseScale, htmlCanvas.height * labelBaseScale, 1]}
    >
      <spriteMaterial transparent>
        <canvasTexture
          attach="map"
          args={[htmlCanvas]}
          minFilter={THREE.LinearMipmapNearestFilter}
          // minFilter={THREE.NearestMipmapLinearFilter}
          // minFilter={THREE.NearestMipmapNearestFilter}
          // minFilter={THREE.LinearFilter}
          wrapS={THREE.ClampToEdgeWrapping}
          wrapT={THREE.ClampToEdgeWrapping}
        />
      </spriteMaterial>
    </sprite>
  );
};
